import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { useSession } from "../auth/client";
import {
  canUserAccessAgency,
  canUserAccessOffer,
  canUserAccessOfferTab,
  getAllowedOffersForUser,
  normalizeOfferAccess,
  type OfferAccessEntry,
} from "../lib/offerAccess";

interface UserData {
  _id?: string;
  email: string;
  name: string;
  role: "admin" | "agency_owner" | "member";
  permissions: string[];
  status: "active" | "pending";
  avatarColor?: string;
  offerAccess: OfferAccessEntry[];
}

interface UserContextType {
  user: UserData | null;
  loading: boolean;
  error: string | null;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  canAccessAgencyView: () => boolean;
  canAccessOffer: (offer: string) => boolean;
  canAccessOfferTab: (offer: string, tabId: string) => boolean;
  getVisibleOffers: (allOffers: string[]) => string[];
  refetch: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export function UserProvider({ children }: { children: ReactNode }) {
  const { data: session, isPending } = useSession();
  const [user, setUser] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchUser = useCallback(async () => {
    if (isPending) {
      setLoading(true);
      return;
    }

    if (!session?.user) {
      setUser(null);
      setLoading(false);
      setHasFetched(true);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch("/api/me");
      const result = await response.json();

      if (result.success) {
        setUser({
          ...result.data,
          offerAccess: normalizeOfferAccess(result.data.offerAccess),
        });
      } else {
        setError(result.error || "Failed to fetch user data");
        setUser(null);
      }
    } catch (err) {
      console.error("Error fetching user:", err);
      setError("Failed to fetch user data");
      setUser(null);
    } finally {
      setLoading(false);
      setHasFetched(true);
    }
  }, [isPending, session?.user?.email]); // Only depend on settled auth state + email

  useEffect(() => {
    if (isPending) {
      setLoading(true);
      return;
    }

    // Only fetch if we haven't fetched yet, or if the user email changed (different user logged in)
    // This prevents refetching when switching browser tabs
    if (!hasFetched || (session?.user?.email && !user)) {
      fetchUser();
    }
  }, [fetchUser, hasFetched, isPending, session?.user?.email, user]);

  const hasPermission = useCallback((permission: string): boolean => {
    if (!user) return false;

    if (user.role === "admin") return true;

    if (user.role === "agency_owner") {
      return user.permissions.includes(permission);
    }

    return user.permissions.includes(permission);
  }, [user]);

  const hasAnyPermission = useCallback((permissions: string[]): boolean => {
    return permissions.some((permission) => hasPermission(permission));
  }, [hasPermission]);

  const canAccessAgencyView = useCallback(() => {
    return canUserAccessAgency(user?.role);
  }, [user?.role]);

  const canAccessOfferValue = useCallback((offer: string) => {
    return canUserAccessOffer(user?.role, user?.offerAccess || [], offer);
  }, [user?.offerAccess, user?.role]);

  const canAccessOfferTabValue = useCallback((offer: string, tabId: string) => {
    return canUserAccessOfferTab(user?.role, user?.offerAccess || [], offer, tabId);
  }, [user?.offerAccess, user?.role]);

  const getVisibleOffers = useCallback((allOffers: string[]) => {
    if (!user) return [];
    if (canUserAccessAgency(user.role)) return allOffers;
    const allowedOffers = new Set(getAllowedOffersForUser(user.role, user.offerAccess));
    return allOffers.filter((offer) => allowedOffers.has(offer));
  }, [user]);

  return (
    <UserContext.Provider value={{
      user,
      loading,
      error,
      hasPermission,
      hasAnyPermission,
      canAccessAgencyView,
      canAccessOffer: canAccessOfferValue,
      canAccessOfferTab: canAccessOfferTabValue,
      getVisibleOffers,
      refetch: fetchUser,
    }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}
